import { createActorContext, useSelector } from '@xstate/react';
import { useCallback, useEffect, useState } from 'react';
import { assign, setup } from 'xstate';

import { isDev } from '@webapp/common/lib/const';

import type { SurveyMachineActor } from '@webapp/survey/src/machine/machine';
import { surveyMachine } from '@webapp/survey/src/machine/machine';
import type { QuestionActorRef } from '@webapp/survey/src/machine/question-machine';

import { assertEventType } from './machine/lib';
import type { SurveyState } from './machine/states';

// enableMapSet();
// setAutoFreeze(true);
// setUseStrictShallowCopy(true);

// https://garden.bradwoods.io/notes/javascript/state-management/xstate/global-state

interface AppContext {
    surveyMachine: SurveyMachineActor;
    devTools: boolean;
    offlineSyncInProgress: boolean;
}

interface AppInput {
    devTools?: boolean;
}

type AppEvents =
    | { type: 'INIT'; domain: string; token: string; individualTries: number }
    | { type: 'START' }
    | { type: 'TOGGLE_DEV'; enabled: boolean };

export const appMachine = setup({
    types: {
        context: {} as AppContext,
        events: {} as AppEvents,
        input: {} as AppInput,
    },
}).createMachine({
    id: 'appMachine',
    initial: 'idle',
    context: ({ input }) => ({
        surveyMachine: null,
        devTools: input.devTools || false,
        offlineSyncInProgress: false,
    }),
    states: {
        idle: {
            entry: [
                assign({
                    surveyMachine: ({ spawn }) => spawn(surveyMachine, { systemId: 'surveyMachine' }),
                }),
            ],
            on: {
                INIT: {
                    actions: [
                        ({ context, event }) => {
                            context.surveyMachine.send({ ...event, type: 'BOOTSTRAP' });
                        },
                    ],
                },
            },
        },
        loaded: {},
    },
    on: {
        TOGGLE_DEV: {
            actions: assign(({ event }) => {
                assertEventType(event, 'TOGGLE_DEV');
                return {
                    devTools: event.enabled,
                };
            }),
        },
    },
});

export const AppMachineContext = createActorContext(appMachine, { input: { devTools: isDev } });

export const useStore = () => {
    const state = AppMachineContext.useSelector((s) => s);
    const sendApp = AppMachineContext.useActorRef();

    const surveyMachine1 = state.context.surveyMachine;
    const surveyMachine1Default = useSelector(surveyMachine1, (state) => state.value);
    const [surveyState, setSurveyState] = useState<SurveyState>(surveyMachine1Default as SurveyState);

    const saveIncomplete = useCallback(
        (sourceQuestion: QuestionActorRef) => {
            surveyMachine1.send({ type: 'SAVE_INCOMPLETE', sourceQuestion });
        },
        [surveyMachine1],
    );

    useEffect(() => {
        const subscription = surveyMachine1.subscribe((state) => {
            setSurveyState(state.value as SurveyState);
        });
        return () => subscription.unsubscribe();
    }, [surveyMachine1]);

    return {
        saveIncomplete,
        devTools: { enabled: true },
        state: surveyState,
        send: surveyMachine1.send,
        useSelector: AppMachineContext.useSelector,
        offlineSyncInProgress: state.context.offlineSyncInProgress,
        sendApp: (event) => sendApp.send(event),
    };
};
